import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Loader2, TrendingUp, Info, X, Users,
} from 'lucide-react';
import apiClient from '../../services/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { useT } from '../../context/LanguageContext';
import { CalendarRoot, isoOf } from '../../components/calendar/PlannerCalendar';

/**
 * The calendar planner — the centre of buffet planning.
 *
 * Nothing on this screen is stored per day. Every price, every set of hours,
 * every closed Monday is RESOLVED on the server by walking the rule stack, and
 * each one arrives knowing which layer produced it. That is what lets the day
 * panel put "Weekends" or "24 Sept only" beside a number instead of leaving a
 * manager to simulate the rules in their head.
 *
 * The rule panel is the point of the screen. Unticking a rule re-resolves the
 * whole month AS IF THAT RULE DID NOT EXIST (?off=), so an invisible stack
 * becomes something you can take apart and put back — which is the only way to
 * answer "what is this rule actually doing to my month".
 *
 * The grid is deliberately quiet: one bold number per cell (the price), the
 * rest secondary, and colour used ONLY to carry meaning — the rule dots and
 * the fill bar's intensity. A dense calendar with decorative colour is a
 * calendar nobody can read.
 */

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/* What a cell's number and fill bar are measuring. Switchable because "which
   days have pricing headroom" is a different question from "which days were
   busy", and a manager needs both off the same grid. */
const METRICS = [
  { key: 'covers', label: 'Covers' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'avg_spend', label: 'Average spend' },
  { key: 'occupancy', label: 'Occupancy' },
];

const money = (n) => (n == null ? '—' : `Rs ${Math.round(n).toLocaleString('en-IN')}`);
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const monthLabel = (d) => d.toLocaleDateString([], { month: 'long', year: 'numeric' });

/* Monday-first grid: the first cell is the Monday on or before the 1st. */
function gridStart(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const shift = (first.getDay() + 6) % 7;
  return new Date(first.getFullYear(), first.getMonth(), 1 - shift);
}

export default function BuffetPlanner() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const { t } = useT();

  const [month, setMonth] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [metric, setMetric] = useState('covers');
  // Rules the manager has unticked. Held here rather than saved: this is a
  // what-if, not an edit — the rule stays active for everyone else.
  const [off, setOff] = useState(() => new Set());
  const [openDay, setOpenDay] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);

  const base = `/users/restaurant/${restaurantId}/buffet-plans/${planId}`;

  const load = useCallback(async () => {
    if (!restaurantId || !planId) return;
    setLoading(true);
    setError('');
    const start = gridStart(month);
    const end = new Date(start);
    end.setDate(end.getDate() + 41);          // six weeks, always a full grid
    try {
      const { data: res } = await apiClient.get(`${base}/calendar/`, {
        params: { from: iso(start), to: iso(end),
          off: [...off].join(',') || undefined },
      });
      setData(res?.success ? res.data : null);
      if (!res?.success) setError(res?.error || 'Could not load the calendar.');
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, planId, month, off, base]);

  useEffect(() => { load(); }, [load]);

  /* The day panel asks for its own detail: the grid carries what a cell needs,
     and every field WITH its source is a much bigger answer than 42 cells
     should each be carrying. */
  useEffect(() => {
    if (!openDay) { setDayDetail(null); return; }
    let alive = true;
    setDayDetail(null);
    apiClient.get(`${base}/day/${openDay}/`)
      .then(({ data: res }) => { if (alive) setDayDetail(res?.success ? res.data : null); })
      .catch(() => { if (alive) setDayDetail(null); });
    return () => { alive = false; };
  }, [openDay, base]);

  const byDate = useMemo(() => {
    const map = {};
    (data?.days || []).forEach((d) => { map[d.date] = d; });
    return map;
  }, [data]);

  /* The fill bar is relative to the biggest day IN VIEW, not to some absolute:
     a quiet month should still show its own shape. */
  const peak = useMemo(() => {
    let top = 0;
    (data?.days || []).forEach((d) => {
      const v = d.history?.[metric];
      if (typeof v === 'number' && v > top) top = v;
    });
    return top;
  }, [data, metric]);

  // Fixed once: a planner left open across midnight moving its underline on
  // the next re-render is a distraction, not a feature.
  const [todayIso] = useState(() => isoOf(new Date()));

  const cells = useMemo(() => {
    const start = gridStart(month);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return { date: iso(d), day: d.getDate(), inMonth: d.getMonth() === month.getMonth() };
    });
  }, [month]);

  const toggleRule = (id) => setOff((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const step = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  const planRange = data?.plan
    ? `${data.plan.date_from} to ${data.plan.date_to}` : '';

  return (
    <div className="p-6 w-full">
      <header className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold leading-none tracking-tight text-foreground">
            {data?.plan?.name || t('plan.calendar.title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {planRange ? t('plan.calendar.runs', { range: planRange })
              : t('plan.calendar.sub')}
          </p>
        </div>
        <button type="button" onClick={() => navigate(-1)}
          className="text-sm text-muted-foreground hover:text-foreground">
          {t('plan.calendar.back')}
        </button>
      </header>

      {error && (
        <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-2.5 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* ── Rule panel: the single most useful control here ─────────────── */}
      <section className="mb-4 rounded-xl border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-sm font-medium text-foreground">{t('plan.rules.title')}</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('plan.rules.hint')}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {METRICS.map((m) => (
              <button key={m.key} type="button" onClick={() => setMetric(m.key)}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  metric === m.key
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}>
                {t(`plan.metric.${m.key}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {(data?.rules || []).length === 0 && (
            <p className="text-xs text-muted-foreground">{t('plan.rules.none')}</p>
          )}
          {(data?.rules || []).map((rule) => {
            const isOff = off.has(rule.id);
            return (
              <button key={rule.id} type="button" onClick={() => toggleRule(rule.id)}
                title={rule.changes.join(' · ')}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  isOff ? 'border-border bg-transparent text-muted-foreground line-through'
                    : 'border-border bg-background text-foreground'
                }`}>
                <span className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: isOff ? 'transparent' : (rule.colour || '#b45309'),
                    boxShadow: isOff ? 'inset 0 0 0 1px currentColor' : 'none' }} />
                {rule.label}
                {!rule.is_active && (
                  <span className="text-[10px] uppercase">{t('plan.rules.inactive')}</span>
                )}
              </button>
            );
          })}
        </div>
        {off.size > 0 && (
          <p className="mt-2 text-xs text-primary">
            {t('plan.rules.previewing', { n: off.size })}{' '}
            <button type="button" className="underline underline-offset-2"
              onClick={() => setOff(new Set())}>{t('plan.rules.restore')}</button>
          </p>
        )}
      </section>

      {/* ── Month navigation ────────────────────────────────────────────── */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => step(-1)} aria-label={t('plan.calendar.prev')}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground">
            <ChevronLeft size={16} />
          </button>
          <span className="min-w-[10rem] text-center text-sm font-medium text-foreground">
            {monthLabel(month)}
          </span>
          <button type="button" onClick={() => step(1)} aria-label={t('plan.calendar.next')}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground">
            <ChevronRight size={16} />
          </button>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* ── The grid ──────────────────────────────────────────────────────
          Wrapped in CalendarRoot for the shared calendar's tokens: this month
          carries prices rather than a square per day, but the MARKS on it —
          the weekend band, a holiday's rule, a festival's ring, today's
          underline — are the same ones every other calendar here draws. */}
      <CalendarRoot className="overflow-x-auto">
        <div className="min-w-[42rem] rounded-xl border border-border bg-card">
          <div className="grid grid-cols-7 border-b border-border">
            {WEEKDAYS.map((d) => (
              <div key={d} className="px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t(`plan.day.${d.toLowerCase()}`)}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((cell) => {
              const day = byDate[cell.date];
              const outside = !cell.inMonth || !day?.in_plan;
              const value = day?.history?.[metric];
              const fill = peak > 0 && typeof value === 'number'
                ? Math.max(4, Math.round((value / peak) * 100)) : 0;
              /* The day's own facts, drawn the way every calendar here draws
                 them: a festival ON its day gets the rule, its run-up the
                 ring, Saturday and Sunday the band, today the underline. */
              const dow = new Date(`${cell.date}T00:00:00`).getDay();
              const onDayItself = (day?.festivals || []).some((f) => f.is_day_itself);
              return (
                <button
                  key={cell.date}
                  type="button"
                  disabled={outside}
                  onClick={() => setOpenDay(cell.date)}
                  className={`rbxcal-cell ${dow === 0 || dow === 6 ? 'we' : ''} ${
                    onDayItself ? 'hol' : (day?.festivals?.length ? 'fest' : '')
                  } ${cell.date === todayIso ? 'today' : ''} h-[5.5rem] border-b border-r border-border p-1.5 text-left transition-colors ${
                    outside ? 'bg-muted/30' : 'hover:bg-accent/40'
                  } ${openDay === cell.date ? 'ring-2 ring-inset ring-primary/40' : ''}`}
                >
                  <div className="flex items-start justify-between gap-1">
                    <span className={`rbxcal-num text-[11px] tabular-nums ${
                      outside ? 'text-muted-foreground/50' : 'text-muted-foreground'
                    }`}>
                      {cell.day}
                    </span>
                    {/* One dot per rule that touched the day — the whole reason
                        the stack is visible at a glance. */}
                    <span className="flex gap-0.5">
                      {(day?.applied || []).slice(0, 4).map((a) => (
                        <span key={a.rule_id} className="h-1.5 w-1.5 rounded-full"
                          style={{ background: a.colour || '#b45309' }}
                          title={a.label} />
                      ))}
                    </span>
                  </div>

                  {day?.in_plan && (
                    <>
                      <div className={`mt-1 text-sm font-semibold tabular-nums ${
                        day.closed ? 'text-muted-foreground line-through' : 'text-foreground'
                      }`}>
                        {day.closed ? t('plan.calendar.closed') : money(day.adult_price)}
                      </div>
                      <div className="mt-0.5 text-[10px] text-muted-foreground tabular-nums">
                        {day.history
                          ? (metric === 'occupancy' ? `${value ?? '—'}%`
                            : metric === 'covers' ? t('plan.calendar.covers', { n: value ?? 0 })
                              : money(value))
                          : t('plan.calendar.noHistory')}
                      </div>
                      {day.festivals?.length > 0 && (
                        <span className="absolute bottom-1 left-1.5 text-[10px]"
                          title={day.festivals.map((f) => f.name).join(', ')}>
                          {day.festivals[0].icon || '•'}
                        </span>
                      )}
                      {day.bookings > 0 && (
                        <span className="absolute bottom-1 right-1.5 inline-flex items-center gap-0.5 text-[10px] text-muted-foreground tabular-nums">
                          <Users size={9} />{day.bookings}
                        </span>
                      )}
                      {/* The fill bar. Intensity only — no hue carries meaning
                          here, so a colour-blind reader loses nothing. */}
                      {fill > 0 && (
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary/70"
                          style={{ width: `${fill}%` }} />
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </CalendarRoot>

      {openDay && (
        <DayPanel date={openDay} detail={dayDetail} onClose={() => setOpenDay(null)} t={t} />
      )}
    </div>
  );
}

/* Every resolved field with the layer that decided it. A base-plan value gets
   a neutral chip, an overridden one an accented chip naming the rule — which
   is the difference between "the price is 1,150" and "the price is 1,150
   because of the weekend rule". */
function DayPanel({ date, detail, onClose, t }) {
  const pretty = new Date(`${date}T00:00:00`).toLocaleDateString(
    [], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const FIELD_LABELS = {
    adult_price: t('plan.field.adultPrice'),
    child_price: t('plan.field.childPrice'),
    sitting_start: t('plan.field.start'),
    sitting_end: t('plan.field.end'),
    sitting_minutes: t('plan.field.length'),
    gap_minutes: t('plan.field.gap'),
    seats: t('plan.field.seats'),
    closed: t('plan.field.closed'),
  };

  return (
    <aside className="fixed inset-y-0 right-0 z-40 w-full max-w-md overflow-y-auto border-l border-border bg-card shadow-xl">
      <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-border bg-card px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">{pretty}</h2>
          {detail?.festivals?.length > 0 && (
            <p className="mt-0.5 text-xs text-primary">
              {detail.festivals.map((f) => f.name).join(' · ')}
            </p>
          )}
        </div>
        <button type="button" onClick={onClose} aria-label={t('plan.day.close')}
          className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
          <X size={16} />
        </button>
      </div>

      {!detail ? (
        <div className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('plan.day.loading')}
        </div>
      ) : (
        <div className="space-y-5 px-5 py-4">
          {/* Resolved fields, each naming its source layer. */}
          <section>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('plan.day.resolved')}
            </h3>
            <dl className="space-y-1.5">
              {Object.entries(detail.fields || {}).map(([name, f]) => (
                <div key={name} className="flex items-center justify-between gap-2 text-sm">
                  <dt className="text-muted-foreground">{FIELD_LABELS[name] || name}</dt>
                  <dd className="flex items-center gap-2">
                    <span className="tabular-nums text-foreground">
                      {name.endsWith('_price') ? money(f.value)
                        : typeof f.value === 'boolean' ? (f.value ? t('common.yes') : t('common.no'))
                          : (f.value ?? '—')}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] ${
                      f.layer === 0
                        ? 'bg-muted text-muted-foreground'
                        : 'bg-primary/10 text-primary'
                    }`}>
                      {f.source}
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Last year, with the basis named — never a bare number. */}
          <section>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('plan.day.lastYear')}
            </h3>
            {detail.history ? (
              <div className="rounded-lg border border-border p-3">
                <p className="text-sm text-foreground tabular-nums">
                  {t('plan.day.covers', { n: detail.history.covers })}
                  {detail.history.occupancy != null && ` · ${detail.history.occupancy}%`}
                  {detail.history.avg_spend != null && ` · ${money(detail.history.avg_spend)}`}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{detail.history.label}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('plan.day.noHistory')}</p>
            )}
            {detail.suggestion && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
                <TrendingUp size={15} className="mt-0.5 shrink-0 text-primary" />
                <div>
                  <p className="text-sm text-foreground">
                    {t('plan.day.suggest', { price: money(detail.suggestion.price),
                      pct: detail.suggestion.uplift_pct })}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {detail.suggestion.because} — {t('plan.day.suggestHint')}
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* The sittings this day actually offers. */}
          <section>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('plan.day.sittings', { n: (detail.sittings || []).length })}
            </h3>
            {detail.closed ? (
              <p className="text-sm text-muted-foreground">{t('plan.day.closedDay')}</p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border">
                {(detail.sittings || []).map((s) => (
                  <li key={s.start} className="flex items-center justify-between px-3 py-1.5 text-sm">
                    <span className="tabular-nums text-foreground">{s.start} – {s.end}</span>
                    <span className="flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                      {s.seats != null && <span>{t('plan.day.seats', { n: s.seats })}</span>}
                      <span className="text-foreground">{money(s.adult_price)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Bookings freeze this day: the plan must not silently reprice them. */}
          {detail.bookings?.length > 0 && (
            <section>
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Info size={12} /> {t('plan.day.booked', { n: detail.bookings.length })}
              </h3>
              <p className="text-xs text-muted-foreground">{t('plan.day.bookedHint')}</p>
              <ul className="mt-1.5 space-y-1">
                {detail.bookings.map((b) => (
                  <li key={b.id} className="flex items-center justify-between text-sm">
                    <span className="text-foreground">{b.name}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {b.sitting || ''} · {t('plan.day.pax', { n: b.pax })}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </aside>
  );
}
